import React from 'react';
import { User, Bot, Clock } from 'lucide-react';

// Typing indicator component
const TypingIndicator = () => (
  <div className="flex items-center space-x-1 py-2">
    <span className="text-gray-500 text-sm">AI is thinking</span>
    <div className="flex space-x-1">
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
    </div>
  </div>
);

const ChatMessage = ({ message, isStreaming = false }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  // Simple markdown formatter for AI messages
  const formatMarkdown = (text) => {
    if (!text) return [];

    const lines = text.split('\n');
    const elements = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Headers
      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={key++} className="text-sm font-semibold mb-1 mt-2 first:mt-0">
            {line.substring(4)}
          </h3>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={key++} className="text-base font-semibold mb-2 mt-3 first:mt-0">
            {line.substring(3)}
          </h2>
        );
      } else if (line.startsWith('# ')) {
        elements.push(
          <h1 key={key++} className="text-lg font-bold mb-2 mt-0">
            {line.substring(2)}
          </h1>
        );
      }
      // List items
      else if (line.startsWith('- ')) {
        elements.push(
          <div key={key++} className="ml-4 mb-1">
            <span className="inline-block w-1 h-1 bg-gray-600 rounded-full mr-2 align-middle"></span>
            {formatInlineMarkdown(line.substring(2))}
          </div>
        );
      }
      // Numbered lists
      else if (/^\d+\.\s/.test(line)) {
        const match = line.match(/^(\d+)\.\s(.+)/);
        if (match) {
          elements.push(
            <div key={key++} className="ml-4 mb-1">
              <span className="inline-block mr-2 text-gray-600 font-medium">{match[1]}.</span>
              {formatInlineMarkdown(match[2])}
            </div>
          );
        }
      }
      // Regular paragraphs
      else if (line.trim()) {
        elements.push(
          <p key={key++} className="mb-2 last:mb-0">
            {formatInlineMarkdown(line)}
          </p>
        );
      }
      // Empty lines
      else {
        elements.push(<br key={key++} />);
      }
    }

    return elements;
  };

  // Format inline markdown (bold, italic)
  const formatInlineMarkdown = (text) => {
    if (!text) return text;

    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining) {
      // Bold text **text**
      const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
      if (boldMatch) {
        const beforeBold = remaining.substring(0, boldMatch.index);
        if (beforeBold) parts.push(beforeBold);
        parts.push(<strong key={key++} className="font-semibold">{boldMatch[1]}</strong>);
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
        continue;
      }

      // Italic text *text*
      const italicMatch = remaining.match(/\*([^*]+)\*/);
      if (italicMatch) {
        const beforeItalic = remaining.substring(0, italicMatch.index);
        if (beforeItalic) parts.push(beforeItalic);
        parts.push(<em key={key++} className="italic">{italicMatch[1]}</em>);
        remaining = remaining.substring(italicMatch.index + italicMatch[0].length);
        continue;
      }

      // No more markdown, add remaining text
      parts.push(remaining);
      break;
    }

    return parts;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`flex gap-3 p-4 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser
          ? 'bg-gray-500 text-white'
          : 'bg-gray-100 text-gray-600'
      }`}>
        {isUser ? <User size={16} /> : <Bot size={16} />}
      </div>

      {/* Message Content */}
      <div className={`flex flex-col max-w-[80%] ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Message Bubble */}
        <div className={`rounded-lg px-4 py-2 ${
          isUser
            ? 'bg-gray-500 text-white'
            : 'bg-gray-100 text-gray-900'
        }`}>
          <div className="break-words">
            {isAssistant ? (
              <div>
                {formatMarkdown(message.content)}
              </div>
            ) : (
              <div className="whitespace-pre-wrap">
                {message.content}
              </div>
            )}
            {isStreaming && !message.content && (
              <TypingIndicator />
            )}
            {isStreaming && message.content && (
              <span className="inline-block w-2 h-4 bg-current opacity-75 animate-pulse ml-1">|</span>
            )}
          </div>
        </div>

        {/* Metadata */}
        <div className={`flex items-center gap-2 mt-1 text-xs text-gray-500 ${
          isUser ? 'flex-row-reverse' : 'flex-row'
        }`}>
          <Clock size={12} />
          <span>
            {message.created_at ? formatTime(message.created_at) : 'Now'}
          </span>
          {isAssistant && message.ai_model && (
            <span className="text-gray-400">
              • {message.ai_model}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;